import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HelpWidget from '../components/HelpWidget';
import { createPaymentOrder, initiateRazorpayPayment, verifyPayment } from '../services/paymentService';
import { validateCoupon } from '../services/couponService';

const ProjectFormPage = ({ variant = 'light' }) => {
  const navigate = useNavigate();
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  // Priority delivery state
  const [priorityDelivery, setPriorityDelivery] = useState(false);

  const [formData, setFormData] = useState({
    deliverableType: '',
    problemDescription: '',
    objective: '',
    proposedSolution: '',
    problemType: '',
    teamName: '',
    numberOfMembers: '',
    teamMembers: [],
    email: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNumberOfMembersChange = (e) => {
    const count = parseInt(e.target.value) || 0;
    const newMembers = Array(count).fill(null).map((_, index) =>
      formData.teamMembers[index] || { name: '', rollNo: '' }
    );
    setFormData(prev => ({
      ...prev,
      numberOfMembers: e.target.value,
      teamMembers: newMembers
    }));
  };

  const handleMemberChange = (index, field, value) => {
    const updatedMembers = [...formData.teamMembers];
    updatedMembers[index] = { ...updatedMembers[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      teamMembers: updatedMembers
    }));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsValidatingCoupon(true);
    setCouponError(null);

    try {
      // Base amount includes priority delivery fee if selected
      let baseAmount = 250000; // ₹2500 in paise
      if (priorityDelivery) {
        baseAmount += 30000; // Add ₹300 for priority delivery
      }
      const result = await validateCoupon(couponCode.trim(), baseAmount);

      if (result.success) {
        setAppliedCoupon(result);
        setCouponError(null);
      }
    } catch (error) {
      setCouponError(error.message || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isReviewMode) {
      // First submission - go to review mode
      setIsReviewMode(true);
      setPaymentError(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Final submission - process payment
      await handlePayment();
    }
  };

  const handlePayment = async () => {
    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      // Step 1: Create order on backend (with coupon if applied)
      const orderResponse = await createPaymentOrder(formData, appliedCoupon ? couponCode : null, priorityDelivery);
      console.log('Order created:', orderResponse);

      // Step 2: Open Razorpay payment modal
      const paymentResult = await initiateRazorpayPayment(orderResponse, formData);
      console.log('Payment completed:', paymentResult);

      // Step 3: Verify payment and store to Google Sheets
      const verifyResponse = await verifyPayment(paymentResult, formData, appliedCoupon ? couponCode : null, priorityDelivery);
      console.log('Payment verified:', verifyResponse);

      // Step 4: Navigate to success page
      navigate('/success');

    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(error.message || 'Payment failed. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  const handleEdit = () => {
    setIsReviewMode(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (variant === 'dark') {
    return (
      <div className="bg-dark-bg text-white min-h-screen flex flex-col font-display">
        <Header variant="dark-form" />

        <main className="flex-grow flex justify-center py-16 md:py-24 px-6">
          <div className="max-w-[600px] w-full flex flex-col gap-12">
            <div className="flex flex-col gap-4">
              <h1 className="text-white text-4xl md:text-5xl font-bold tracking-tight">
                {isReviewMode ? 'Review Your Submission' : 'Project Submission'}
              </h1>
              <p className="text-gray-500 text-lg font-normal">
                {isReviewMode
                  ? 'Please review your details carefully before proceeding to payment.'
                  : 'Submit your project details. You will receive confirmation at the provided email address.'}
              </p>
            </div>

            {isReviewMode && (
              <div className="bg-dark-card border border-blue-500/30 rounded-xl p-6 flex items-start gap-3">
                <span className="material-symbols-outlined text-blue-400 text-2xl mt-0.5">verified</span>
                <div className="flex flex-col gap-1">
                  <p className="text-white font-semibold">Review Mode</p>
                  <p className="text-gray-400 text-sm">
                    Your form has been validated. Review your information below and click "Pay ₹2500" when ready. <span className="text-green-400 font-semibold">Limited time discount!</span>
                  </p>
                </div>
              </div>
            )}

            {paymentError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 flex items-start gap-3">
                <span className="material-symbols-outlined text-red-400 text-2xl mt-0.5">error</span>
                <div className="flex flex-col gap-1">
                  <p className="text-red-400 font-semibold">Payment Error</p>
                  <p className="text-gray-400 text-sm">{paymentError}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-10">
              {/* What to Build Section */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3 pb-3 border-b border-border-gray">
                  <span className="material-symbols-outlined text-white text-2xl">build</span>
                  <h2 className="text-white text-xl font-bold tracking-tight">What Do You Want Built?</h2>
                </div>

                <div className="flex flex-col gap-3">
                  <label className="text-white text-sm font-bold uppercase tracking-wider">Deliverable Type</label>
                  {isReviewMode ? (
                    <div className="w-full rounded-xl border border-border-gray bg-dark-card h-14 px-5 flex items-center text-white text-base">
                      {formData.deliverableType === 'website' && 'Website'}
                      {formData.deliverableType === 'web-app' && 'Web Application'}
                      {formData.deliverableType === 'mobile-app' && 'Mobile App (iOS/Android)'}
                      {formData.deliverableType === 'desktop-app' && 'Desktop Application'}
                      {formData.deliverableType === 'api' && 'API/Backend Service'}
                      {formData.deliverableType === 'ml-model' && 'Machine Learning Model'}
                      {formData.deliverableType === 'data-analysis' && 'Data Analysis/Dashboard'}
                      {formData.deliverableType === 'blockchain' && 'Blockchain/Smart Contract'}
                      {formData.deliverableType === 'game' && 'Game'}
                      {formData.deliverableType === 'other' && 'Other'}
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <select
                          className="w-full appearance-none rounded-xl border border-border-gray bg-dark-bg h-14 px-5 text-white text-base transition-all focus:outline-none focus:border-white"
                          name="deliverableType"
                          value={formData.deliverableType}
                          onChange={handleChange}
                          required
                        >
                          <option disabled value="">What would you like us to build?</option>
                          <option value="website">Website</option>
                          <option value="web-app">Web Application</option>
                          <option value="mobile-app">Mobile App (iOS/Android)</option>
                          <option value="desktop-app">Desktop Application</option>
                          <option value="api">API/Backend Service</option>
                          <option value="ml-model">Machine Learning Model</option>
                          <option value="data-analysis">Data Analysis/Dashboard</option>
                          <option value="blockchain">Blockchain/Smart Contract</option>
                          <option value="game">Game</option>
                          <option value="other">Other</option>
                        </select>
                        <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                          expand_more
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm flex items-start gap-2 mt-1">
                        <span className="material-symbols-outlined text-base mt-0.5">lightbulb</span>
                        <span>Select what you want us to create for your project.</span>
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Team Information Section */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3 pb-3 border-b border-border-gray">
                  <span className="material-symbols-outlined text-white text-2xl">group</span>
                  <h2 className="text-white text-xl font-bold tracking-tight">Team Information</h2>
                </div>

                <div className="flex flex-col gap-3">
                  <label className="text-white text-sm font-bold uppercase tracking-wider">Team Name</label>
                  <input
                    className={`w-full rounded-xl border border-border-gray h-14 px-5 text-white text-base transition-all focus:outline-none focus:border-white ${isReviewMode ? 'bg-dark-card cursor-not-allowed' : 'bg-dark-bg placeholder:text-gray-700'}`}
                    name="teamName"
                    placeholder="Enter your team name"
                    required
                    type="text"
                    value={formData.teamName}
                    onChange={handleChange}
                    readOnly={isReviewMode}
                  />
                </div>

                <div className="flex flex-col gap-3">
                  <label className="text-white text-sm font-bold uppercase tracking-wider">Number of Team Members</label>
                  <input
                    className={`w-full rounded-xl border border-border-gray h-14 px-5 text-white text-base transition-all focus:outline-none focus:border-white ${isReviewMode ? 'bg-dark-card cursor-not-allowed' : 'bg-dark-bg placeholder:text-gray-700'}`}
                    name="numberOfMembers"
                    placeholder="Enter number of members (1-10)"
                    required
                    type="number"
                    min="1"
                    max="10"
                    value={formData.numberOfMembers}
                    onChange={handleNumberOfMembersChange}
                    readOnly={isReviewMode}
                  />
                </div>

                {formData.teamMembers.length > 0 && (
                  <div className="flex flex-col gap-4">
                    <p className="text-gray-400 text-sm">Enter details for each team member:</p>
                    {formData.teamMembers.map((member, index) => (
                      <div key={index} className="border border-border-gray rounded-xl p-6 flex flex-col gap-4 bg-dark-card">
                        <h3 className="text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                          <span className="material-symbols-outlined text-lg">person</span>
                          Member {index + 1}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <label className="text-gray-400 text-xs font-medium uppercase tracking-wider">Name</label>
                            <input
                              className={`w-full rounded-lg border border-border-gray h-12 px-4 text-white text-base transition-all focus:outline-none focus:border-white ${isReviewMode ? 'bg-dark-card cursor-not-allowed' : 'bg-dark-bg placeholder:text-gray-700'}`}
                              placeholder="Member name"
                              required
                              type="text"
                              value={member.name}
                              onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                              readOnly={isReviewMode}
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="text-gray-400 text-xs font-medium uppercase tracking-wider">Roll Number</label>
                            <input
                              className={`w-full rounded-lg border border-border-gray h-12 px-4 text-white text-base transition-all focus:outline-none focus:border-white ${isReviewMode ? 'bg-dark-card cursor-not-allowed' : 'bg-dark-bg placeholder:text-gray-700'}`}
                              placeholder="Roll number"
                              required
                              type="text"
                              value={member.rollNo}
                              onChange={(e) => handleMemberChange(index, 'rollNo', e.target.value)}
                              readOnly={isReviewMode}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Contact Information Section */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3 pb-3 border-b border-border-gray">
                  <span className="material-symbols-outlined text-white text-2xl">mail</span>
                  <h2 className="text-white text-xl font-bold tracking-tight">Contact Information</h2>
                </div>

                <div className="flex flex-col gap-3">
                  <label className="text-white text-sm font-bold uppercase tracking-wider">Contact Email</label>
                  <input
                    className={`w-full rounded-xl border border-border-gray h-14 px-5 text-white text-base transition-all focus:outline-none focus:border-white ${isReviewMode ? 'bg-dark-card cursor-not-allowed' : 'bg-dark-bg placeholder:text-gray-700'}`}
                    name="email"
                    placeholder="team@example.com"
                    required
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    readOnly={isReviewMode}
                  />
                  {!isReviewMode && (
                    <div className="bg-dark-card border border-border-gray rounded-lg p-4 flex items-start gap-3 mt-2">
                      <span className="material-symbols-outlined text-blue-400 text-xl mt-0.5">info</span>
                      <p className="text-gray-400 text-sm">
                        Project confirmation and all updates will be sent to this email address. Please ensure it's correct.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Project Details Section */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3 pb-3 border-b border-border-gray">
                  <span className="material-symbols-outlined text-white text-2xl">description</span>
                  <h2 className="text-white text-xl font-bold tracking-tight">Project Details</h2>
                </div>

                <div className="flex flex-col gap-3">
                  <label className="text-white text-sm font-bold uppercase tracking-wider">Problem Type</label>
                  {isReviewMode ? (
                    <div className="w-full rounded-xl border border-border-gray bg-dark-card h-14 px-5 flex items-center text-white text-base">
                      {formData.problemType === 'university-project' && 'University Project / Academic'}
                      {formData.problemType === 'final-year' && 'Final Year Project'}
                      {formData.problemType === 'deep-tech' && 'Deep Tech'}
                      {formData.problemType === 'ai-ml' && 'AI / ML'}
                      {formData.problemType === 'research' && 'Research & Development'}
                      {formData.problemType === 'iot' && 'IoT / Hardware'}
                      {formData.problemType === 'blockchain' && 'Blockchain / Web3'}
                      {formData.problemType === 'data-science' && 'Data Science / Analytics'}
                      {formData.problemType === 'web-dev' && 'Web Development'}
                      {formData.problemType === 'mobile-dev' && 'Mobile Development'}
                      {formData.problemType === 'startup' && 'Startup / Business Idea'}
                      {formData.problemType === 'hackathon' && 'Hackathon Project'}
                      {formData.problemType === 'other' && 'Other'}
                    </div>
                  ) : (
                    <div className="relative">
                      <select
                        className="w-full appearance-none rounded-xl border border-border-gray bg-dark-bg h-14 px-5 text-white text-base transition-all focus:outline-none focus:border-white"
                        name="problemType"
                        value={formData.problemType}
                        onChange={handleChange}
                        required
                      >
                        <option disabled value="">Select problem type</option>
                        <option value="university-project">University Project / Academic</option>
                        <option value="final-year">Final Year Project</option>
                        <option value="deep-tech">Deep Tech</option>
                        <option value="ai-ml">AI / ML</option>
                        <option value="research">Research & Development</option>
                        <option value="iot">IoT / Hardware</option>
                        <option value="blockchain">Blockchain / Web3</option>
                        <option value="data-science">Data Science / Analytics</option>
                        <option value="web-dev">Web Development</option>
                        <option value="mobile-dev">Mobile Development</option>
                        <option value="startup">Startup / Business Idea</option>
                        <option value="hackathon">Hackathon Project</option>
                        <option value="other">Other</option>
                      </select>
                      <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                        expand_more
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  <label className="text-white text-sm font-bold uppercase tracking-wider">Problem Description</label>
                  <textarea
                    className={`w-full rounded-xl border border-border-gray p-5 text-white text-base transition-all resize-none min-h-[120px] focus:outline-none focus:border-white ${isReviewMode ? 'bg-dark-card cursor-not-allowed' : 'bg-dark-bg placeholder:text-gray-700'}`}
                    name="problemDescription"
                    placeholder="Describe the problem you're trying to solve..."
                    required
                    value={formData.problemDescription}
                    onChange={handleChange}
                    readOnly={isReviewMode}
                  ></textarea>
                </div>

                <div className="flex flex-col gap-3">
                  <label className="text-white text-sm font-bold uppercase tracking-wider">Objective</label>
                  <textarea
                    className={`w-full rounded-xl border border-border-gray p-5 text-white text-base transition-all resize-none min-h-[100px] focus:outline-none focus:border-white ${isReviewMode ? 'bg-dark-card cursor-not-allowed' : 'bg-dark-bg placeholder:text-gray-700'}`}
                    name="objective"
                    placeholder="What is the main objective of this project..."
                    required
                    value={formData.objective}
                    onChange={handleChange}
                    readOnly={isReviewMode}
                  ></textarea>
                </div>

                <div className="flex flex-col gap-3">
                  <label className="text-white text-sm font-bold uppercase tracking-wider">Proposed Solution</label>
                  <textarea
                    className={`w-full rounded-xl border border-border-gray p-5 text-white text-base transition-all resize-none min-h-[120px] focus:outline-none focus:border-white ${isReviewMode ? 'bg-dark-card cursor-not-allowed' : 'bg-dark-bg placeholder:text-gray-700'}`}
                    name="proposedSolution"
                    placeholder="Describe your proposed solution or approach..."
                    required
                    value={formData.proposedSolution}
                    onChange={handleChange}
                    readOnly={isReviewMode}
                  ></textarea>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center gap-6">
                {isReviewMode ? (
                  <>
                    {/* Priority Delivery Section */}
                    <div className="w-full">
                      <label
                        className={`group relative flex items-start gap-4 p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 overflow-hidden ${priorityDelivery
                            ? 'bg-gradient-to-br from-yellow-500/20 via-orange-500/15 to-amber-500/20 border-yellow-400 shadow-lg shadow-yellow-500/20'
                            : 'bg-dark-card border-border-gray hover:border-yellow-500/30 hover:shadow-md hover:shadow-yellow-500/10'
                          }`}
                      >
                        {/* Animated background shimmer effect */}
                        {priorityDelivery && (
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/10 to-transparent animate-shimmer"
                            style={{
                              backgroundSize: '200% 100%',
                              animation: 'shimmer 3s infinite'
                            }}
                          ></div>
                        )}

                        {/* Checkbox */}
                        <div className="relative z-10">
                          <input
                            type="checkbox"
                            checked={priorityDelivery}
                            onChange={(e) => {
                              setPriorityDelivery(e.target.checked);
                              // Reset coupon when priority changes
                              if (appliedCoupon) {
                                setAppliedCoupon(null);
                                setCouponCode('');
                              }
                            }}
                            className="mt-1 w-6 h-6 rounded-md border-2 border-yellow-500 text-yellow-500 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-dark-bg cursor-pointer transition-all"
                          />
                        </div>

                        {/* Content */}
                        <div className="flex-1 relative z-10">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`${priorityDelivery ? 'animate-pulse' : ''}`}>
                              <span className="material-symbols-outlined text-yellow-400 text-3xl drop-shadow-lg">bolt</span>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-white text-xl font-bold tracking-tight">Priority Delivery</h3>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                                  +₹300
                                </span>
                                <span className="bg-green-500/20 text-green-400 text-xs font-bold px-2 py-1 rounded-full border border-green-500/30">
                                  SAVE TIME
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2 mt-3">
                            <div className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-yellow-400 text-sm">schedule</span>
                              <p className="text-gray-300 text-sm">
                                Lightning-fast delivery in <span className="text-yellow-400 font-bold text-base">12 hours</span>
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-gray-500 text-sm line-through">schedule</span>
                              <p className="text-gray-500 text-xs line-through">
                                Standard: 3-4 days
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Check icon with animation */}
                        {priorityDelivery && (
                          <div className="absolute top-4 right-4 z-10 animate-bounce-slow">
                            <div className="relative">
                              <div className="absolute inset-0 bg-yellow-400 rounded-full blur-md opacity-50"></div>
                              <span className="material-symbols-outlined text-yellow-400 text-2xl relative">check_circle</span>
                            </div>
                          </div>
                        )}

                        {/* Corner accent */}
                        {priorityDelivery && (
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-yellow-400/20 to-transparent rounded-bl-full"></div>
                        )}
                      </label>
                    </div>

                    {/* Coupon Code Section */}
                    <div className="w-full flex flex-col gap-4">
                      <div className="flex flex-col gap-3">
                        <label className="text-white text-sm font-bold uppercase tracking-wider">Have a Coupon Code?</label>
                        {!appliedCoupon ? (
                          <div className="flex gap-3">
                            <input
                              className="flex-1 rounded-xl border border-border-gray bg-dark-bg h-14 px-5 text-white text-base transition-all focus:outline-none focus:border-white placeholder:text-gray-700"
                              placeholder="Enter coupon code"
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              disabled={isValidatingCoupon}
                            />
                            <button
                              type="button"
                              onClick={handleApplyCoupon}
                              disabled={isValidatingCoupon || !couponCode.trim()}
                              className="bg-blue-500 hover:bg-blue-600 text-white h-14 px-8 rounded-xl text-base font-bold tracking-tight transition-all active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                              {isValidatingCoupon ? (
                                <>
                                  <span className="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                                  Validating...
                                </>
                              ) : (
                                <>
                                  <span className="material-symbols-outlined text-lg">check_circle</span>
                                  Apply
                                </>
                              )}
                            </button>
                          </div>
                        ) : (
                          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="material-symbols-outlined text-green-400 text-2xl">check_circle</span>
                              <div>
                                <p className="text-white font-semibold">{appliedCoupon.couponCode}</p>
                                <p className="text-green-400 text-sm">{appliedCoupon.couponDescription || 'Discount applied'}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveCoupon}
                              className="text-gray-400 hover:text-white transition-colors"
                            >
                              <span className="material-symbols-outlined">close</span>
                            </button>
                          </div>
                        )}
                        {couponError && (
                          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                            <span className="material-symbols-outlined text-red-400 text-xl mt-0.5">error</span>
                            <p className="text-red-400 text-sm">{couponError}</p>
                          </div>
                        )}
                      </div>

                      {/* Price Summary */}
                      {appliedCoupon && (
                        <div className="bg-dark-card border border-border-gray rounded-xl p-6 flex flex-col gap-3">
                          <div className="flex justify-between items-center text-gray-400">
                            <span>Original Price</span>
                            <span className="line-through">₹{(appliedCoupon.originalAmount / 100).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center text-green-400">
                            <span>Discount</span>
                            <span>-₹{(appliedCoupon.discountAmount / 100).toFixed(2)}</span>
                          </div>
                          <div className="border-t border-border-gray pt-3 flex justify-between items-center text-white text-xl font-bold">
                            <span>Final Price</span>
                            <span>₹{(appliedCoupon.finalAmount / 100).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={handleEdit}
                      className="w-full bg-transparent border-2 border-white hover:bg-white hover:text-black text-white h-14 rounded-xl text-base font-bold tracking-tight transition-all active:scale-[0.99] flex items-center justify-center gap-3"
                    >
                      <span className="material-symbols-outlined">edit</span>
                      Edit Details
                    </button>
                    <button
                      className="w-full bg-white hover:bg-gray-200 text-black h-16 rounded-xl text-lg font-bold tracking-tight transition-all active:scale-[0.99] flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      type="submit"
                      disabled={isProcessingPayment}
                    >
                      {isProcessingPayment ? (
                        <>
                          <span className="material-symbols-outlined animate-spin">progress_activity</span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined">credit_card</span>
                          <div className="flex items-center gap-2">
                            {appliedCoupon ? (
                              <>
                                <span className="line-through text-gray-500 text-base">₹{(appliedCoupon.originalAmount / 100).toFixed(0)}</span>
                                <span>Pay ₹{(appliedCoupon.finalAmount / 100).toFixed(0)}</span>
                              </>
                            ) : (
                              <>
                                <span className="line-through text-gray-500 text-base">₹4000</span>
                                <span>Pay ₹{priorityDelivery ? '2800' : '2500'}</span>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </button>
                    <p className="text-gray-500 text-sm text-center flex items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-base">lock</span>
                      Secure payment via Razorpay. Your data is encrypted.
                    </p>
                  </>
                ) : (
                  <>
                    <button
                      className="w-full bg-white hover:bg-gray-200 text-black h-16 rounded-xl text-lg font-bold tracking-tight transition-all active:scale-[0.99] flex items-center justify-center gap-3"
                      type="submit"
                    >
                      <span className="material-symbols-outlined">preview</span>
                      Review Submission
                    </button>
                    <p className="text-gray-500 text-sm text-center flex items-center justify-center gap-2">
                      <span className="material-symbols-outlined text-base">info</span>
                      You'll be able to review before payment.
                    </p>
                  </>
                )}
              </div>
            </form>
          </div>
        </main>

        <Footer variant="dark-form" />
        <HelpWidget />
      </div>
    );
  }

  // Light variant (default)
  return (
    <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col font-display transition-colors duration-200">
      <Header variant="light" />

      <main className="flex-grow flex justify-center py-12 md:py-20 px-6">
        <div className="max-w-[640px] w-full flex flex-col gap-10">
          <div className="flex flex-col gap-3 text-center md:text-left">
            <h1 className="text-[#0e141b] dark:text-white text-4xl md:text-5xl font-black leading-tight tracking-[-0.033em]">Project Submission</h1>
            <p className="text-[#4e7397] dark:text-slate-400 text-lg font-normal leading-normal">Tell us about your project and let's bring it to life.</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-[#0e141b] dark:text-slate-200 text-base font-semibold">Name</label>
              <input
                className="w-full rounded-lg border border-[#d0dbe7] dark:border-slate-700 bg-white dark:bg-background-dark focus:ring-2 focus:ring-primary/20 focus:border-primary h-14 px-4 text-[#0e141b] dark:text-white text-base placeholder:text-[#4e7397] transition-all"
                name="name"
                placeholder="Enter your full name"
                required
                type="text"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[#0e141b] dark:text-slate-200 text-base font-semibold">Email</label>
              <input
                className="w-full rounded-lg border border-[#d0dbe7] dark:border-slate-700 bg-white dark:bg-background-dark focus:ring-2 focus:ring-primary/20 focus:border-primary h-14 px-4 text-[#0e141b] dark:text-white text-base placeholder:text-[#4e7397] transition-all"
                name="email"
                placeholder="you@example.com"
                required
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[#0e141b] dark:text-slate-200 text-base font-semibold">Project Type</label>
              <div className="relative">
                <select
                  className="w-full appearance-none rounded-lg border border-[#d0dbe7] dark:border-slate-700 bg-white dark:bg-background-dark focus:ring-2 focus:ring-primary/20 focus:border-primary h-14 px-4 text-[#0e141b] dark:text-white text-base transition-all"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                >
                  <option disabled value="">Select project type</option>
                  <option value="web">Web Application</option>
                  <option value="mobile">Mobile Application</option>
                  <option value="uiux">UI/UX Design</option>
                  <option value="other">Other</option>
                </select>
                <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#4e7397]">
                  expand_more
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[#0e141b] dark:text-slate-200 text-base font-semibold">Preferred Technology</label>
              <input
                className="w-full rounded-lg border border-[#d0dbe7] dark:border-slate-700 bg-white dark:bg-background-dark focus:ring-2 focus:ring-primary/20 focus:border-primary h-14 px-4 text-[#0e141b] dark:text-white text-base placeholder:text-[#4e7397] transition-all"
                name="technology"
                placeholder="e.g. React, Node.js, Flutter"
                type="text"
                value={formData.technology}
                onChange={handleChange}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-[#0e141b] dark:text-slate-200 text-base font-semibold">Budget Range</label>
                <input
                  className="w-full rounded-lg border border-[#d0dbe7] dark:border-slate-700 bg-white dark:bg-background-dark focus:ring-2 focus:ring-primary/20 focus:border-primary h-14 px-4 text-[#0e141b] dark:text-white text-base placeholder:text-[#4e7397] transition-all"
                  name="budget"
                  placeholder="e.g. $5k - $10k"
                  type="text"
                  value={formData.budget}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[#0e141b] dark:text-slate-200 text-base font-semibold">Deadline</label>
                <input
                  className="w-full rounded-lg border border-[#d0dbe7] dark:border-slate-700 bg-white dark:bg-background-dark focus:ring-2 focus:ring-primary/20 focus:border-primary h-14 px-4 text-[#0e141b] dark:text-white text-base placeholder:text-[#4e7397] transition-all"
                  name="deadline"
                  placeholder="e.g. 3 months"
                  type="text"
                  value={formData.deadline}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[#0e141b] dark:text-slate-200 text-base font-semibold">Project Description</label>
              <textarea
                className="w-full rounded-lg border border-[#d0dbe7] dark:border-slate-700 bg-white dark:bg-background-dark focus:ring-2 focus:ring-primary/20 focus:border-primary p-4 text-[#0e141b] dark:text-white text-base placeholder:text-[#4e7397] transition-all resize-none"
                name="description"
                placeholder="Tell us about the features, users, and goals..."
                rows="5"
                value={formData.description}
                onChange={handleChange}
              ></textarea>
            </div>

            <div className="mt-4 flex flex-col items-center gap-4">
              <button
                className="w-full bg-primary hover:bg-primary/90 text-white h-14 rounded-lg text-lg font-bold tracking-wide transition-all shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
                type="submit"
              >
                <span className="material-symbols-outlined">payments</span>
                Pay &amp; Submit
              </button>
              <div className="flex flex-col items-center gap-1">
                <p className="text-[#4e7397] dark:text-slate-500 text-sm flex items-center gap-1">
                  <span className="material-symbols-outlined text-xs">lock</span>
                  Secure payment via Razorpay.
                </p>
                <p className="text-[#4e7397] dark:text-slate-500 text-xs">
                  Your details are automatically saved.
                </p>
              </div>
            </div>
          </form>
        </div>
      </main>

      <Footer variant="light" />
    </div>
  );
};

export default ProjectFormPage;
